// API Endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  LOGIN: "/2fa/login/start",
  VERIFY_2FA: "/2fa/login/verify-otp",
  VERIFY_BACKUP: "/2fa/login/verify-backup",
  RESEND_LOGIN_2FA: "/2fa/login/resend",
  REGISTRATION_START: "/users/registration",
  REGISTRATION_VERIFY_TOKEN: "/users/registration/validation",
  REGISTRATION_PHONE_SEND: "/users/registration/phone/send",
  REGISTRATION_PHONE_VERIFY: "/users/registration/phone/verify",
  FORGOT_PASSWORD: "/password/forgot",
  RESET_PASSWORD: "/password/reset",
  LOGOUT: "/token",
  LOGOUT_ALL: "/token/all",
  GET_CURRENT_USER: "/me",
  SEND_2FA_CODE: "/2fa/setup/send",
  VERIFY_2FA_SETUP: "/2fa/setup/verify",
  REGENERATE_BACKUP_CODES: "/2fa/backup-codes/regenerate",

  // Tenant management
  TENANTS: "/tenants",
} as const;

// Navigation paths
export const ROUTES = {
  // Auth routes
  LOGIN: "/login",
  REGISTRATION: "/register",
  REGISTRATION_WITH_TOKEN: "/registration/:token",
  REGISTRATION_SUCCESS: "/registration-success",
  REGISTRATION_ERROR: "/registration-error",
  VERIFICATION: "/verification",
  TWO_FACTOR_AUTH: "/two-factor-auth",
  BACKUP_CODE: "/backup-code",
  FORGOT_PASSWORD: "/forgot-password",
  CREATE_PASSWORD: "/create-password",
  CREATE_PASSWORD_WITH_TOKEN: "/reset-password/:token",
  CHECK_EMAIL: "/check-email",
  SAVE_BACKUP_CODES: "/save-backup-codes",
  SAVE_BACKUP_CODES_SUCCESS: "/save-backup-codes-success",
  SUCCESS: "/success",

  // Main app routes
  DASHBOARD: "/dashboard",
  SESSIONS: "/sessions",
  SESSION_DETAILS: "/sessions/:id",
  PATIENTS: "/patients",
  PATIENT_PROFILE: "/patients/:id",
  EXTERNAL_PATIENT: "/external-patient/:externalId",
  TREATMENT_PLAN: "/treatment-plan/:id",
  TREATMENT_PLAN_EDIT: "/treatment-plan/:id/edit",
  USER_MANAGEMENT: "/user-management",
  USER_PROFILE: "/user-management/:id",
  USER_PROFILE_EDIT: "/user-management/:id/edit",
  MANAGE_ORGANIZATION: "/manage-organization",
  AUDIT_LOGS: "/audit-logs",
  TENANT_MANAGEMENT: "/tenant-management",
  PROFILE: "/profile",
  PROFILE_EDIT: "/profile/edit",

  // Landing page (public)
  LANDING: "/landing",

  // Demo / Twilio test flow (public, no auth required)
  DEMO: "/sms-verification",
  DEMO_VERIFY: "/sms-verification/verify",
  DEMO_SUCCESS: "/sms-verification/success",

  // Public routes
  OTP: "/otp",
  TERMS: "/terms",
  PRIVACY: "/privacy",
  SMS_POLICY: "/sms-policy",

  // Treatment plan review (public - for patient approval via QR code)
  TREATMENT_PLAN_REVIEW: "/treatment-plan/review/:token",
  TREATMENT_PLAN_REVIEW_APPROVED: "/treatment-plan/review/approved",
  TREATMENT_PLAN_REVIEW_REJECTED: "/treatment-plan/review/rejected",
} as const;

export const ENV = {
  isProduction: process.env.REACT_APP_ENV === "production",
} as const;

// Form validation constants
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  CODE_LENGTH: 6,
  RESEND_COUNTDOWN: 60,
  MAX_RETRIES: 3,
} as const;

// UI constants
export const UI = {
  SNACKBAR_DURATION: 3000,
  MODAL_ANIMATION_DURATION: 300,
  DEBOUNCE_DELAY: 300,
  INACTIVITY_TIMEOUT_MINUTES: 120,
} as const;

export const STORAGE_KEYS = {
  TOKEN: "token",
  REGISTRATION_TOKEN: "registrationToken",
  EMAIL: "email",
  PHONE_NUMBER: "phoneNumber",
  FORGOT_PASSWORD_EMAIL: "forgotPasswordEmail",
  REGISTRATION_FORM_DATA: "registrationFormData",
} as const;

export const COOKIE_KEYS = {
  REFRESH_TOKEN: "rt",
  ACCESS_TOKEN: "at",
  EMAIL_HINT: "email_hint",
  PHONE_HINT: "phone_hint",
  FORGOT_EMAIL_HINT: "forgot_email_hint",
} as const;

// Re-export role enum and constants — single source of truth
export {
  UserRole,
  ROLE_TITLES,
  ROLE_OPTIONS,
  ROLE_FILTER_OPTIONS,
  AUDIT_ROLE_FILTER_OPTIONS,
} from "./roles";

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: "Network error. Please check your connection.",
  UNAUTHORIZED: "Unauthorized. Please log in again.",
  FORBIDDEN: "Access denied.",
  NOT_FOUND: "Resource not found.",
  SERVER_ERROR: "Server error. Please try again later.",
  VALIDATION_ERROR: "Please check your input and try again.",
} as const;
